import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

/**
 * Modal shell: backdrop + centered panel + standard header.
 * Used by CommitmentModal, DelegateModal, FollowUpModal, the create modals, etc.
 *
 *  <ModalShell open={open} onClose={...} title="New Commitment" maxWidth={480}>
 *    <form>...</form>
 *  </ModalShell>
 *
 * Optional `icon` + `accent` render the calendar-create style accent tile
 * in the header (soft accent wash behind the icon).
 */
export function ModalShell({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  accent = 'var(--pd-focus)',
  children,
  footer,
  maxWidth = 480,
  testId,
}) {
  useBodyScrollLock(open);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: 'var(--pd-overlay)', backdropFilter: 'blur(4px)' }}
    >
      <div
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        className="flex min-h-full items-center justify-center px-4 py-8"
      >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full rounded-[24px] overflow-hidden animate-fade-in"
        style={{
          maxWidth,
          background: 'var(--pd-popover)',
          border: '1px solid var(--pd-popover-border)',
          boxShadow: 'var(--pd-shadow-3)',
          maxHeight: 'calc(100vh - 4rem)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {title ? (
          <div
            className="flex items-center gap-3 px-5 py-4 flex-shrink-0"
            style={{ borderBottom: '1px solid var(--pd-border)' }}
          >
            {Icon ? (
              <div
                className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: `color-mix(in srgb, ${accent} 10%, transparent)` }}
              >
                <Icon className="w-5 h-5" style={{ color: accent }} />
              </div>
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-bold truncate" style={{ color: 'var(--pd-text)' }}>{title}</div>
              {subtitle ? (
                <div className="text-[10px] mt-0.5" style={{ color: 'var(--pd-subtle)' }}>{subtitle}</div>
              ) : null}
            </div>
            <button
              data-testid={testId ? `${testId}-close` : undefined}
              onClick={onClose}
              className="p-1 rounded-lg flex-shrink-0"
              style={{ color: 'var(--pd-muted)' }}
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : null}
        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
        {footer ? (
          <div
            className="px-5 py-3 flex-shrink-0 flex items-center justify-end gap-2"
            style={{ borderTop: '1px solid var(--pd-border)' }}
          >
            {footer}
          </div>
        ) : null}
      </div>
      </div>
    </div>
  );
}
